/**
 * The official roster of פלוגת שינוע 99 — the twenty soldiers the company
 * actually holds, seeded into the `soldiers` table on first run.
 *
 * This is real unit data, not a fixture: `src/core/mock-fleet.ts` remains the
 * verification-only set that no application module may import, and the vehicle
 * pool is still filled by the unit itself. Only the roster ships.
 *
 * Seeding runs once and never overwrites: `seedSoldierRoster()` in
 * `src/db/fleet-store.ts` writes the list only into an empty table, so a
 * soldier the operator has since edited — or deleted — is left exactly as they
 * left them.
 *
 * That is not enough for a device that was already running before the roster
 * existed: its table is not empty, so the seed never fires and the operator is
 * left looking at demo records. `reconcileSoldierRoster()` is the one-shot
 * migration that closes that gap — see {@link ROSTER_REVISION} and
 * {@link isLegacyDemoSoldier} at the foot of this file.
 *
 * Ids are derived from the personal number (`sld-7533454`), which is what makes
 * the seed stable across reloads and keeps it clear of the `sld-102`-style
 * fixture ids.
 *
 * A licence is recorded the way the matcher reasons about it — by weight class.
 * The mapping from the class letter written on the military licence is a
 * bijection, so nothing is lost: see {@link LICENSE_CLASS_WEIGHT}.
 */
import { DEFAULT_SOLDIER_ROLE } from '../types/fleet';
import type { Soldier, SoldierRole, WeightCategory } from '../types/fleet';
import { SUPER_ADMIN_ID } from './mock-users';

/** The one company on this installation. */
export const ROSTER_COMPANY = 'פלוגת שינוע 99';

/** Licence classes as written on a military driving licence. */
export const LICENSE_CLASSES = ['B', 'C1', 'C'] as const;

export type LicenseClass = (typeof LICENSE_CLASSES)[number];

/**
 * Licence class → the weight class the matching engine reads. The scale is
 * cumulative on both sides, so the correspondence is exact:
 * `B` ≤ 3.5 טון, `C1` 3.5–12 טון, `C` מעל 12 טון.
 */
export const LICENSE_CLASS_WEIGHT: Record<LicenseClass, WeightCategory> = {
  B: 'light',
  C1: 'medium',
  C: 'heavy',
};

/** The moment the roster was filed — a fixed stamp, so the seed is stable. */
const SEEDED_AT = '2026-01-01T08:00:00.000Z';

/** One line of the roster as the company keeps it. */
interface RosterEntry {
  fullName: string;
  /** Military personal number (מ.א). */
  personalNumber: string;
  /** Mobile (נייד), in the local `05…` form. */
  phone: string;
  license: LicenseClass;
  /** Defaults to נהג — the job of all but the company commander. */
  role?: SoldierRole;
  /** Left empty until the company files each soldier under a platoon. */
  platoon?: string;
  /** Set for a soldier who also holds an account in the user directory. */
  userId?: string;
}

const ROSTER: readonly RosterEntry[] = [
  { fullName: 'נתי כהן', personalNumber: '6179000', phone: '0503434384', license: 'C' },
  { fullName: 'אשר אמר', personalNumber: '7678224', phone: '0509901145', license: 'C' },
  { fullName: 'יורם דמרי', personalNumber: '5644109', phone: '0502025412', license: 'C' },
  { fullName: 'שי דומר', personalNumber: '7424657', phone: '0502430830', license: 'C' },
  { fullName: 'איציק בן נעים', personalNumber: '6392906', phone: '0503779101', license: 'C' },
  { fullName: 'יוסי דהן', personalNumber: '7270813', phone: '0527542000', license: 'C' },
  { fullName: 'ברוך זרד', personalNumber: '7526298', phone: '0559600564', license: 'C' },
  {
    // The owner of the installation — the super-admin account in the directory.
    fullName: 'בן פרץ',
    personalNumber: '7533454',
    phone: '0524063968',
    license: 'C',
    role: 'company_commander',
    platoon: 'מפקדה',
    userId: SUPER_ADMIN_ID,
  },
  { fullName: 'שלומי דהן', personalNumber: '6109256', phone: '0548116367', license: 'C' },
  { fullName: 'חיים פשרל', personalNumber: '5045968', phone: '0528892893', license: 'C' },
  { fullName: 'אופיר כהן', personalNumber: '8708576', phone: '0585954055', license: 'C' },
  { fullName: 'אלימלך גראומן', personalNumber: '5980238', phone: '0586664255', license: 'C1' },
  { fullName: 'מוטי בן סימון', personalNumber: '8038034', phone: '0522956191', license: 'C1' },
  { fullName: 'משה חיים ביביאן', personalNumber: '7041013', phone: '0543261555', license: 'C' },
  { fullName: 'עוזי הררי', personalNumber: '8108037', phone: '0556629821', license: 'B' },
  { fullName: 'נועם כהן', personalNumber: '8332012', phone: '0542133316', license: 'C' },
  { fullName: 'ליאור שטרון', personalNumber: '7397881', phone: '0504640650', license: 'B' },
  { fullName: 'אביאל בן עזרא', personalNumber: '5316303', phone: '0523236541', license: 'C' },
  { fullName: 'שרה חיון', personalNumber: '5374154', phone: '0556666300', license: 'B' },
  { fullName: 'אריאל אדרי', personalNumber: '7393678', phone: '0536761856', license: 'C' },
];

/** The stable id a roster record is written under. */
export const soldierSeedId = (personalNumber: string): string => `sld-${personalNumber}`;

function toSoldier(entry: RosterEntry): Soldier {
  return {
    id: soldierSeedId(entry.personalNumber),
    ...(entry.userId ? { userId: entry.userId } : {}),
    fullName: entry.fullName,
    personalNumber: entry.personalNumber,
    role: entry.role ?? DEFAULT_SOLDIER_ROLE,
    company: ROSTER_COMPANY,
    platoon: entry.platoon ?? '',
    phone: entry.phone,
    licenseWeightCategory: LICENSE_CLASS_WEIGHT[entry.license],
    // Endorsements are not part of the roster the company handed over; they are
    // filled in from the soldier's own licence when there is one to read.
    licensePermits: [],
    // No renewal date came with the roster. `null` is "not recorded", and the
    // matcher reads it as a licence that has not expired.
    licenseExpiresAt: null,
    availability: 'available',
    temporaryStatus: null,
    disallowedVehicleIds: [],
    createdAt: SEEDED_AT,
    updatedAt: SEEDED_AT,
  };
}

/** The twenty seeded soldiers, in the order the company lists them. */
export const SEED_SOLDIERS: readonly Soldier[] = ROSTER.map(toSoldier);

/** How many soldiers a clean installation opens with. */
export const SEED_SOLDIER_COUNT = SEED_SOLDIERS.length;

/** Every מ.א on the official roster — the set the purge measures against. */
export const ROSTER_PERSONAL_NUMBERS: ReadonlySet<string> = new Set(
  SEED_SOLDIERS.map((soldier) => soldier.personalNumber),
);

/** Every id on the official roster. */
export const ROSTER_SOLDIER_IDS: ReadonlySet<string> = new Set(
  SEED_SOLDIERS.map((soldier) => soldier.id),
);

// ── Legacy demo data ─────────────────────────────────────────────────

/**
 * The revision of the roster the local database has been reconciled against.
 *
 * Bump it when the roster itself changes and every device already in the field
 * has to be brought back onto it. `reconcileSoldierRoster()` runs once per
 * revision and then records it, so an operator's later edits are never undone
 * by a second pass.
 *
 * Revision 2 carries the spelling correction below.
 */
export const ROSTER_REVISION = 2;

/**
 * Names an earlier revision of the roster spelled wrongly, keyed by מ.א.
 *
 * A backfill cannot reach these: the soldier is already on the device under the
 * right מ.א, so nothing is missing and nothing is written. The correction is
 * therefore applied by name — and only when the record still reads exactly as
 * the bad revision wrote it, so a name the operator has since typed themselves
 * is never overwritten.
 */
export const ROSTER_NAME_CORRECTIONS: readonly {
  personalNumber: string;
  from: string;
  to: string;
}[] = [{ personalNumber: '5374154', from: 'שרה חיוון', to: 'שרה חיון' }];

/**
 * Soldier ids an early build wrote into `soldiers` before the company roster
 * existed — the verification fixtures of `src/core/mock-fleet.ts`, which that
 * build seeded, and the `sld-demo-…` placeholders alongside them.
 *
 * The ids are spelled out here rather than imported: `src/core/mock-fleet.ts`
 * is quarantined from the application (suite 32), and this list has to outlive
 * the fixtures anyway — it describes what is *on people's phones*, not what the
 * project currently ships.
 */
export const LEGACY_DEMO_SOLDIER_IDS: readonly string[] = [
  'sld-demo-01',
  'sld-102',
  'sld-204',
  'sld-311',
  'sld-458',
  'sld-604',
  'sld-777',
  'sld-830',
];

/** Placeholder names an early build wrote in place of a real soldier. */
export const LEGACY_DEMO_SOLDIER_NAMES: readonly string[] = ['ישראל ישראלי'];

const LEGACY_IDS = new Set(LEGACY_DEMO_SOLDIER_IDS);
const LEGACY_NAMES = new Set(LEGACY_DEMO_SOLDIER_NAMES);

/** The shape the test needs — any stored soldier satisfies it. */
export interface LegacySoldierProbe {
  id?: string;
  fullName?: string;
  personalNumber?: string;
}

/**
 * Whether a stored record is demo data left behind by an older build.
 *
 * The test is deliberately narrow. A soldier the company actually holds is
 * never demo data, whatever id an older build filed them under, so the roster's
 * own מ.א short-circuits everything below it; past that, only a known
 * placeholder id or a known placeholder name qualifies. A record the operator
 * typed in themselves matches none of them — ids they create are
 * `sld-<uuid>` — so the purge can never reach the unit's own roster.
 */
export function isLegacyDemoSoldier(soldier: LegacySoldierProbe | null | undefined): boolean {
  if (!soldier) return false;
  const personalNumber = String(soldier.personalNumber ?? '').trim();
  if (ROSTER_PERSONAL_NUMBERS.has(personalNumber)) return false;

  const id = String(soldier.id ?? '').trim();
  if (ROSTER_SOLDIER_IDS.has(id)) return false;
  if (/^sld-demo(-|$)/i.test(id)) return true;
  if (LEGACY_IDS.has(id)) return true;

  return LEGACY_NAMES.has(String(soldier.fullName ?? '').trim());
}
